import type {
  CardFieldChangeEvent,
  CardFieldElement,
  CardFieldElementEventName,
  CardFieldElementType,
  CardNumberElementOptions,
  PaypercutElementsAppearance,
  ElementsErrorEvent,
} from '../types/elements';
import { Emitter } from '../utils/emitter';
import { generateId } from '../utils/generate-id';
import type { CardCollectionFieldChange } from './card-collection-provider';
import type { CardFieldCopy } from './card-field-copy';
import type { CardFieldComponentType, ElementsComponentEventPayload } from './controller-protocol';
import { CardFieldSurface } from './card-field-surface';
import { isElementVisible } from './element-visibility';
import type { ElementsGroupContext } from './group';

// Provider initialization and a concrete field mount each have one bounded
// recovery attempt. This outer deadline covers both tiers plus controller-copy
// delivery while guaranteeing that initialization cannot remain indefinite.
export const CARD_FIELD_INITIALIZATION_TIMEOUT_MS = 40_000;

const componentTypeFor = (type: CardFieldElementType): CardFieldComponentType => {
  switch (type) {
    case 'cardNumber':
      return 'card_number';
    case 'cardExpiry':
      return 'card_expiry';
    case 'cardCvc':
      return 'card_cvc';
  }
};

const resolveContainer = (target: string | HTMLElement): HTMLElement => {
  const container = typeof target === 'string' ? document.querySelector(target) : target;
  if (!(container instanceof HTMLElement)) {
    throw new Error(
      `[Paypercut Elements] Card field mount target was not found: ${String(target)}`,
    );
  }
  return container;
};

const publicChange = (
  type: CardFieldElementType,
  change: CardCollectionFieldChange,
  copy: CardFieldCopy,
): CardFieldChangeEvent => {
  return {
    complete: change.complete,
    empty: change.empty,
    ...(type === 'cardNumber' && change.brand ? { brand: change.brand } : {}),
    ...(change.errorType
      ? {
          error: {
            type: change.errorType,
            message: copy.errors[change.errorType],
          },
        }
      : {}),
  };
};

export class CardFieldElementImpl implements CardFieldElement {
  readonly componentId = generateId();
  readonly componentGeneration = 0;
  readonly componentType: CardFieldComponentType;
  private readonly emitter = new Emitter<CardFieldElementEventName>();
  private surface: CardFieldSurface | null = null;
  private mounted = false;
  private ready = false;
  private destroyed = false;
  private mountGeneration = 0;
  private initializationTimeoutId: number | null = null;
  private presentationSignature: string | null = null;
  private copy: CardFieldCopy | null = null;

  constructor(
    readonly type: CardFieldElementType,
    private readonly group: ElementsGroupContext,
    private readonly options?: CardNumberElementOptions,
  ) {
    this.componentType = componentTypeFor(type);
  }

  mount(target: string | HTMLElement): void {
    if (this.destroyed) {
      throw new Error('[Paypercut Elements] Cannot mount a destroyed card field.');
    }
    if (this.mounted) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      throw new Error('[Paypercut Elements] Card fields can only be mounted in a browser.');
    }
    if (this.group.isDestroyed()) {
      throw new Error('[Paypercut Elements] Cannot mount an Element from a destroyed group.');
    }

    const container = resolveContainer(target);
    const generation = this.mountGeneration + 1;
    this.mountGeneration = generation;
    this.mounted = true;
    this.group.markComponentMounted();

    this.armInitializationWatch(generation);

    void this.initialize(container, generation);
  }

  private async initialize(container: HTMLElement, generation: number): Promise<void> {
    try {
      const copy = await this.group.resolveCardFieldCopy(this.type);
      if (!this.isCurrentMount(generation)) return;

      const surface = new CardFieldSurface(container, {
        type: this.type,
        copy,
        appearance: this.group.getAppearance(),
        ...(this.type === 'cardNumber' ? { showIcon: this.options?.showIcon } : {}),
      });
      surface.providerMount.id = `paypercut-${this.type}-provider-${this.componentId}`;
      this.surface = surface;
      this.copy = copy;
      const presentation = surface.getPresentation();
      this.presentationSignature = JSON.stringify(presentation);

      await this.group.mountCardField({
        id: this.componentId,
        type: this.type,
        target: surface.providerMount,
        ariaLabel: copy.ariaLabel,
        placeholder: presentation.placeholder,
        presentation,
        events: {
          onReady: () => this.onProviderReady(generation),
          onChange: (change) => this.onProviderChange(generation, change),
          onFocus: () => this.onProviderFocus(generation),
          onBlur: () => this.onProviderBlur(generation),
          onError: (error) => {
            if (this.isCurrentMount(generation)) this.emitter.emit('error', error);
          },
        },
      });
      if (this.isCurrentMount(generation)) this.group.onCardFieldAvailabilityChange();
    } catch (error: unknown) {
      if (!this.isCurrentMount(generation)) return;
      const details = (error as { details?: ElementsErrorEvent }).details;
      if (details) {
        this.failInitialization(details.message, details);
        return;
      }
      this.failInitialization('The secure card field could not be initialized.');
    }
  }

  on(event: 'ready', handler: () => void): () => void;
  on(event: 'change', handler: (event: CardFieldChangeEvent) => void): () => void;
  on(event: 'focus' | 'blur', handler: () => void): () => void;
  on(event: 'error', handler: (event: ElementsErrorEvent) => void): () => void;
  on(
    event: CardFieldElementEventName,
    handler: ((event: CardFieldChangeEvent | ElementsErrorEvent) => void) | (() => void),
  ): () => void {
    return this.emitter.on(event, handler);
  }

  clear(): void {
    this.command('clear');
  }

  focus(): void {
    this.command('focus');
  }

  blur(): void {
    this.command('blur');
  }

  getComponentOptions(): Record<string, never> {
    return {};
  }

  revealSubmissionError(errorType: 'incomplete' | 'invalid'): void {
    if (this.destroyed || !this.mounted || !this.ready) return;
    this.surface?.revealValidationError(errorType);
    try {
      this.group.commandCardField(this.componentId, this.componentType, 'focus');
    } catch {
      // The visible validation state is still useful if the provider lost focus support.
    }
  }

  hasActiveSubmission(): boolean {
    return false;
  }

  handleControllerError(error: ElementsErrorEvent): void {
    if (this.destroyed) return;
    if (!this.ready) {
      this.failInitialization(error.message, error);
      return;
    }
    this.ready = false;
    this.emitter.emit('error', error);
    this.group.onCardFieldAvailabilityChange();
  }

  handleControllerEvent(payload: ElementsComponentEventPayload): void {
    if (payload.event === 'error') this.handleControllerError(payload);
  }

  updateAppearance(appearance: PaypercutElementsAppearance | undefined): void {
    if (this.destroyed || !this.surface) return;
    this.surface.updateAppearance(appearance);
    this.syncPresentation();
  }

  announceAppearanceReady(): void {
    if (this.destroyed || !this.mounted || !this.surface) return;
    this.ready = true;
    this.clearInitializationWatch();
    this.surface.handleReady();
    this.emitter.emit('ready');
    this.group.onCardFieldAvailabilityChange();
  }

  beginAppearanceTransition(): void {
    if (this.destroyed || !this.mounted || !this.surface) return;
    this.ready = false;
    this.clearInitializationWatch();
    this.surface.beginAppearanceTransition();
    this.armInitializationWatch(this.mountGeneration);
    this.group.onCardFieldAvailabilityChange();
  }

  isMounted(): boolean {
    return this.mounted && !this.destroyed;
  }

  isVisible(): boolean {
    return isElementVisible(this.surface?.root ?? null);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.mountGeneration += 1;
    this.ready = false;
    this.clearInitializationWatch();
    this.group.destroyCardField(this.componentId);
    this.group.onCardFieldAvailabilityChange();
    this.surface?.destroy();
    this.surface = null;
    this.copy = null;
    this.presentationSignature = null;
    this.mounted = false;
    this.emitter.clear();
    this.group.remove(this);
  }

  private command(action: 'clear' | 'focus' | 'blur'): void {
    if (this.destroyed) {
      throw new Error(`[Paypercut Elements] Cannot ${action} a destroyed card field.`);
    }
    if (!this.mounted || !this.ready) {
      throw new Error(`[Paypercut Elements] Cannot ${action} a card field before it is ready.`);
    }
    this.group.commandCardField(this.componentId, this.componentType, action);
  }

  private onProviderReady(generation: number): void {
    if (!this.isCurrentMount(generation) || this.ready) return;
    this.ready = true;
    this.clearInitializationWatch();
    this.surface?.handleReady();
    this.emitter.emit('ready');
    this.group.onCardFieldAvailabilityChange();
  }

  private onProviderChange(generation: number, change: CardCollectionFieldChange): void {
    if (!this.isCurrentMount(generation) || !this.copy) return;
    this.group.onCardFieldValueChange();
    const normalized = publicChange(this.type, change, this.copy);
    this.surface?.handleChange(normalized);
    this.syncPresentation();
    this.emitter.emit('change', normalized);
  }

  private onProviderFocus(generation: number): void {
    if (!this.isCurrentMount(generation)) return;
    this.surface?.handleFocus();
    this.syncPresentation();
    this.emitter.emit('focus');
  }

  private onProviderBlur(generation: number): void {
    if (!this.isCurrentMount(generation)) return;
    this.surface?.handleBlur();
    this.syncPresentation();
    this.emitter.emit('blur');
  }

  private failInitialization(
    message: string,
    details: ElementsErrorEvent = {
      code: 'elements_provider_initialization_failed',
      message,
      recoverable: true,
    },
  ): void {
    if (this.destroyed || this.ready || !this.mounted) return;
    this.rollbackMount();
    this.emitter.emit('error', details);
  }

  private rollbackMount(): void {
    this.mountGeneration += 1;
    this.clearInitializationWatch();
    this.group.destroyCardField(this.componentId);
    this.surface?.destroy();
    this.surface = null;
    this.copy = null;
    this.presentationSignature = null;
    this.mounted = false;
    this.ready = false;
    this.group.onCardFieldAvailabilityChange();
  }

  private isCurrentMount(generation: number): boolean {
    return !this.destroyed && this.mounted && this.mountGeneration === generation;
  }

  private syncPresentation(): void {
    if (!this.surface || !this.mounted || this.destroyed) return;
    const presentation = this.surface.getPresentation();
    const signature = JSON.stringify(presentation);
    if (signature === this.presentationSignature) return;
    this.presentationSignature = signature;
    this.group.updateCardFieldPresentation(this.componentId, presentation);
  }

  private clearInitializationWatch(): void {
    if (this.initializationTimeoutId !== null && typeof window !== 'undefined') {
      window.clearTimeout(this.initializationTimeoutId);
    }
    this.initializationTimeoutId = null;
  }

  private armInitializationWatch(generation: number): void {
    this.clearInitializationWatch();
    this.initializationTimeoutId = window.setTimeout(() => {
      if (this.isCurrentMount(generation) && !this.ready) {
        this.failInitialization('The secure card field did not become ready in time.');
      }
    }, CARD_FIELD_INITIALIZATION_TIMEOUT_MS);
  }
}
